import React, { useState, useEffect } from 'react';
import { getPlanningDetail, updatePlanningBulk } from '../../services/api';

// Deriva el nombre del día y la fecha corta directamente desde el string de fecha
// Usa UTC para evitar que el timezone desplace el día
function dayLabel(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  const d = new Date(Date.UTC(year, month - 1, day));
  const nombre = d.toLocaleDateString('es-MX', { weekday: 'long', timeZone: 'UTC' });
  const corta = d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  return {
    nombre: nombre.charAt(0).toUpperCase() + nombre.slice(1),
    corta: corta.replace('.', '').toUpperCase(),
  };
}

function formatDateFull(dateStr) {
  if (!dateStr) return '—';
  // Soporta "2020-05-18" y "Mon, 18 May 2020 00:00:00 GMT"
  const isISO = /^\d{4}-\d{2}-\d{2}/.test(dateStr);
  let d;
  if (isISO) {
    const [year, month, day] = dateStr.split('-').map(Number);
    d = new Date(Date.UTC(year, month - 1, day));
    return d.toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  d = new Date(dateStr);
  return d.toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

// Extrae el orden correcto de días a partir de los días del primer registro (ordenados por fecha)
function extractDayOrder(activities) {
  if (!activities || activities.length === 0) return [];
  const firstAct = activities[0];
  if (!firstAct.days || firstAct.days.length === 0) return [];
  return [...firstAct.days]
    .sort((a, b) => new Date(a.day) - new Date(b.day))
    .map(d => d.day);
}

// Mapea días de una actividad a un objeto { [dateStr]: dayObj }
function mapDays(days) {
  const map = {};
  (days || []).forEach(d => { map[d.day] = d; });
  return map;
}

export default function PlanningDetail({ planning, onBack }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orderedDays, setOrderedDays] = useState([]);

  const [isEditing, setIsEditing] = useState(false);
  const [editedActivities, setEditedActivities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (!planning?.id) return;
    setLoading(true);
    setError('');
    getPlanningDetail(planning.id)
      .then(data => {
        const list = Array.isArray(data) ? data : data.data || [];
        setActivities(list);
        setOrderedDays(extractDayOrder(list));
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [planning]);

  const startEdit = () => {
    setEditedActivities(JSON.parse(JSON.stringify(activities)));
    setSaveError('');
    setSaveSuccess(false);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditedActivities([]);
    setSaveError('');
  };

  const updatePeople = (actIdx, dayStr, value) => {
    setEditedActivities(prev => {
      const next = [...prev];
      const act = { ...next[actIdx] };
      act.days = act.days.map(d =>
        d.day === dayStr ? { ...d, people_no: value } : d
      );
      next[actIdx] = act;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const bulk = editedActivities.map(act => {
        const objectiveWeekly = act.days.reduce(
          (sum, d) => sum + (parseFloat(d.people_no) || 0), 0
        );
        return {
          activity: act.activity,
          activity_id: act.activity_id,
          activity_sub_category_id: act.activity_sub_category_id ?? null,
          days: act.days.map(d => ({
            ...d,
            people_no: d.people_no != null ? String(d.people_no) : '0',
            estimated_units: d.people_no && parseFloat(d.people_no) > 0
              ? String(parseFloat(d.people_no) * (act.activity?.performance || 1))
              : '0',
            time: d.people_no && parseFloat(d.people_no) > 0 ? (d.time ?? null) : null,
          })),
          id: act.id,
          planning_id: act.planning_id,
          priority: act.priority,
          uuid: act.uuid,
          objective_weekly: objectiveWeekly,
          objective_weekly_destajo: act.objective_weekly_destajo ?? 0,
        };
      });
      await updatePlanningBulk(planning.id, bulk);
      setActivities(editedActivities);
      setSaveSuccess(true);
      setIsEditing(false);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const displayActivities = isEditing ? editedActivities : activities;

  const totalPeopleByDay = (dayStr) =>
    displayActivities.reduce((sum, act) => {
      const map = mapDays(act.days);
      return sum + (parseFloat(map[dayStr]?.people_no) || 0);
    }, 0);

  const totalPeopleRow = (act) => {
    const map = mapDays(act.days);
    return orderedDays.reduce((sum, d) => sum + (parseFloat(map[d]?.people_no) || 0), 0);
  };

  const grandTotal = orderedDays.reduce((sum, d) => sum + totalPeopleByDay(d), 0);

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <button className="btn btn-ghost btn-sm" onClick={onBack} disabled={saving}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Volver al listado
        </button>

        <div style={styles.headerMeta}>
          <span style={styles.chip}>{planning.business_unit_name || '—'}</span>
          <span style={{ ...styles.chip, color: 'var(--accent-green)', borderColor: 'rgba(61,214,140,0.3)', background: 'rgba(61,214,140,0.08)' }}>
            {planning.crop_name || '—'}
          </span>
          <span style={{ ...styles.chip, color: 'var(--accent-blue)', borderColor: 'rgba(78,158,255,0.3)', background: 'rgba(78,158,255,0.08)' }}>
            {planning.week_name || '—'}
          </span>
          <span style={{ ...styles.chip, color: 'var(--accent)', borderColor: 'rgba(245,197,24,0.3)', background: 'var(--accent-glow)' }}>
            {planning.lot_name || '—'}
          </span>
        </div>

        {/* Avance + botones edición */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {!isEditing && (
            <div style={styles.progressWrap}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Avance</span>
              <div style={styles.progressBar}>
                <div style={{
                  ...styles.progressFill,
                  width: `${planning.completed_percent || 0}%`,
                  background: (planning.completed_percent || 0) > 80 ? 'var(--accent-green)' : 'var(--accent)',
                }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', minWidth: 32 }}>
                {planning.completed_percent || 0}%
              </span>
            </div>
          )}

          {!loading && !isEditing && (
            <button className="btn btn-secondary btn-sm" onClick={startEdit}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Editar
            </button>
          )}

          {isEditing && (
            <>
              <button className="btn btn-ghost btn-sm" onClick={cancelEdit} disabled={saving}>
                Cancelar
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <><div className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} /> Guardando...</>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                      <polyline points="17 21 17 13 7 13 7 21"/>
                      <polyline points="7 3 7 8 15 8"/>
                    </svg>
                    Guardar cambios
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div style={styles.summaryRow}>
        {[
          { label: 'Total actividades', value: planning.total_activities ?? activities.length, icon: '📋' },
          { label: 'Colaboradores planificados', value: Math.round(planning.people_in_planning || 0), icon: '👥' },
          {
            label: 'Período',
            value: planning.from_date
              ? `${formatDateFull(planning.from_date)} — ${formatDateFull(planning.to_date)}`
              : orderedDays.length > 0
                ? `${formatDateFull(orderedDays[0])} — ${formatDateFull(orderedDays[orderedDays.length - 1])}`
                : '—',
            icon: '📅',
          },
        ].map((s, i) => (
          <div key={i} style={styles.summaryCard} className="card">
            <span style={{ fontSize: 20 }}>{s.icon}</span>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {s.label}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginTop: 2 }}>
                {s.value}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error" style={{ margin: '0 32px' }}>{error}</div>
      )}
      {saveError && (
        <div className="alert alert-error" style={{ margin: '0 32px' }}>{saveError}</div>
      )}
      {saveSuccess && (
        <div className="alert alert-success" style={{ margin: '0 32px' }}>
          Planeación actualizada correctamente
        </div>
      )}

      {/* Table */}
      <div style={{ margin: '0 32px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <div className="spinner" />
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={styles.stickyTh}>Actividad</th>
                    {orderedDays.map((day) => {
                      const { nombre, corta } = dayLabel(day);
                      return (
                        <th key={day} style={{ textAlign: 'center', minWidth: 88 }}>
                          <div style={{ fontWeight: 600 }}>{nombre}</div>
                          <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-muted)', marginTop: 1 }}>
                            {corta}
                          </div>
                        </th>
                      );
                    })}
                    <th style={{ textAlign: 'center', minWidth: 90, color: 'var(--accent)' }}>
                      Plan. semanal
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayActivities.length === 0 && (
                    <tr>
                      <td colSpan={orderedDays.length + 2} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 48 }}>
                        No hay actividades registradas en esta planeación
                      </td>
                    </tr>
                  )}

                  {displayActivities.map((act, actIdx) => {
                    const dayMap = mapDays(act.days);
                    const actName = act.activity?.name || `Actividad #${act.activity_id}`;
                    const weeklyTotal = totalPeopleRow(act);

                    return (
                      <tr key={act.id || actIdx}>
                        {/* Actividad */}
                        <td style={styles.stickyTd}>
                          <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--text-primary)' }}>
                            {actName}
                          </div>
                          {act.activity?.family_activity_name && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                              {act.activity.family_activity_name}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                            <span style={styles.priorityBadge}>#{act.priority}</span>
                            {act.activity?.is_control && (
                              <span style={{ ...styles.priorityBadge, background: 'rgba(78,158,255,0.12)', color: 'var(--accent-blue)' }}>
                                Control
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Días */}
                        {orderedDays.map(day => {
                          const d = dayMap[day];
                          const hasPeople = d?.people_no != null && parseFloat(d.people_no) > 0;

                          if (isEditing) {
                            return (
                              <td key={day} style={{ padding: '6px 5px' }}>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  className="input-field"
                                  style={{ padding: '5px 6px', textAlign: 'center', fontSize: 13, width: 70 }}
                                  placeholder="0"
                                  value={d?.people_no != null && d.people_no !== '0' ? d.people_no : ''}
                                  onChange={e => updatePeople(actIdx, day, e.target.value)}
                                />
                              </td>
                            );
                          }

                          return (
                            <td key={day} style={{ textAlign: 'center', padding: '8px 6px' }}>
                              {hasPeople ? (
                                <div style={styles.dayCell}>
                                  <div style={styles.dayCellPeople}>{d.people_no}</div>
                                  {d.time != null && (
                                    <div style={styles.dayCellTime}>{d.time}h</div>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: 'var(--text-muted)', fontSize: 18, lineHeight: 1 }}>—</span>
                              )}
                            </td>
                          );
                        })}

                        {/* Total semanal */}
                        <td style={{ textAlign: 'center', fontWeight: 700, fontSize: 15, color: 'var(--accent)' }}>
                          {weeklyTotal > 0 ? weeklyTotal : <span style={{ color: 'var(--text-muted)', fontSize: 18 }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Fila totales */}
                  {displayActivities.length > 0 && (
                    <tr style={{ background: 'var(--bg-primary)', borderTop: '2px solid var(--border-light)' }}>
                      <td style={{ ...styles.stickyTd, background: 'var(--bg-primary)', fontWeight: 700, fontSize: 12, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Total por día
                      </td>
                      {orderedDays.map(day => {
                        const total = totalPeopleByDay(day);
                        return (
                          <td key={day} style={{ textAlign: 'center', fontWeight: 700, fontSize: 14, color: total > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                            {total > 0 ? total : '—'}
                          </td>
                        );
                      })}
                      <td style={{ textAlign: 'center', fontWeight: 800, fontSize: 16, color: 'var(--accent)' }}>
                        {grandTotal > 0 ? grandTotal : '—'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: '24px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '0 32px',
    flexWrap: 'wrap',
  },
  headerMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    flex: 1,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500,
    background: '#fff',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-light)',
  },
  progressWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  progressBar: {
    width: 100,
    height: 6,
    background: 'var(--border-light)',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.5s ease',
  },
  summaryRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 14,
    margin: '0 32px',
  },
  summaryCard: {
    padding: '16px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  tableWrap: {
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
  },
  stickyTh: {
    position: 'sticky',
    left: 0,
    background: 'var(--bg-primary)',
    zIndex: 2,
    width: 220,
    minWidth: 200,
  },
  stickyTd: {
    position: 'sticky',
    left: 0,
    background: '#fff',
    zIndex: 1,
    width: 220,
    minWidth: 200,
  },
  dayCell: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'rgba(245,197,24,0.08)',
    border: '1px solid rgba(245,197,24,0.2)',
    borderRadius: 8,
    padding: '5px 10px',
    minWidth: 48,
  },
  dayCellPeople: {
    fontWeight: 700,
    fontSize: 15,
    color: 'var(--accent)',
    lineHeight: 1,
  },
  dayCellTime: {
    fontSize: 10,
    color: 'var(--text-muted)',
    marginTop: 2,
  },
  priorityBadge: {
    display: 'inline-flex',
    padding: '1px 6px',
    borderRadius: 10,
    fontSize: 10,
    fontWeight: 600,
    background: 'rgba(245,197,24,0.1)',
    color: 'var(--accent)',
  },
};
